import React, { useState, useEffect } from 'react';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import EmployeeIdConfigForm from '../components/employee/EmployeeIdConfigForm';
import { employeeApi } from '../api/employeeApi';
import { Hash, Sparkles, Edit2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

const EmployeeIdSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await employeeApi.getIdConfigs();
      setConfigs(res.data || []);
    } catch (err) {
      toast.error('Failed to fetch Employee ID configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSaveConfig = async (formData) => {
    if (!selectedConfig) return;
    setSubmitting(true);
    try {
      await employeeApi.updateIdConfig(selectedConfig.companyId._id, formData);
      toast.success(`Updated Employee ID prefix for ${selectedConfig.companyId.name}`);
      setSelectedConfig(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err.message || 'Failed to update ID config');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      header: 'Company Tenant',
      accessor: 'companyId',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.companyId?.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={row.companyId?.name}
            className="w-10 h-10 rounded-xl object-cover border border-slate-100"
          />
          <div>
            <p className="font-semibold text-slate-900 font-display">{row.companyId?.name || 'Company'}</p>
            <p className="text-xs text-slate-500">Total Employees: {row.companyId?.currentEmployeeCount || 0}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Configured Prefix',
      accessor: 'prefix',
      cell: (row) => (
        <span className="font-mono font-bold text-xs px-3 py-1 rounded-lg bg-green-600 text-white shadow-sm">
          {row.prefix}
        </span>
      )
    },
    {
      header: 'Sequence Counter',
      accessor: 'currentSequence',
      cell: (row) => (
        <span className="font-mono text-xs font-semibold text-slate-600">
          #{row.currentSequence}
        </span>
      )
    },
    {
      header: 'Next Generated ID Preview',
      accessor: '_id',
      cell: (row) => {
        const nextSeq = String(row.currentSequence + 1).padStart(4, '0');
        const nextId = `${row.prefix}${nextSeq}`;
        return (
          <div className="flex items-center space-x-1.5 font-mono font-bold text-xs text-white bg-green-600 shadow-sm px-3 py-1 rounded-lg w-fit">
            <Sparkles className="w-3.5 h-3.5" />
            <span>{nextId}</span>
          </div>
        );
      }
    },
    {
      header: 'Actions',
      accessor: 'action',
      cell: (row) => (
        <button
          onClick={() => setSelectedConfig(row)}
          className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 transition flex items-center space-x-1 text-xs"
        >
          <Edit2 className="w-3.5 h-3.5 text-green-700" />
          <span>Edit Prefix</span>
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-extrabold text-slate-900 font-display">Employee ID Settings</h2>
        <p className="text-xs text-slate-500 mt-1">
          Configure static prefixes and atomic sequence rules per company tenant
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-xs text-slate-500">Loading configurations...</div>
      ) : configs.length === 0 ? (
        <div className="p-12 text-center bg-white/50 rounded-2xl border border-slate-100 text-xs text-slate-500">
          No configurations available.
        </div>
      ) : (
        <Table columns={columns} data={configs} />
      )}

      {/* Edit Config Modal */}
      <Modal
        isOpen={!!selectedConfig}
        onClose={() => setSelectedConfig(null)}
        title={`Configure Employee ID Prefix: ${selectedConfig?.companyId?.name || ''}`}
        maxWidth="max-w-md"
      >
        <EmployeeIdConfigForm
          config={selectedConfig}
          onSubmit={handleSaveConfig}
          onCancel={() => setSelectedConfig(null)}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default EmployeeIdSettings;
